import _ from 'lodash';
import { IObservableArray, makeAutoObservable } from 'mobx';
import { logger } from 'services/Logger';
import { Quaternion, Vector3 } from 'three';
import { ST } from 'types';

export interface PosData {
  loc: Vector3;
  rot: Quaternion;
}

const Step2PosData = (d: ST.PlayStep): PosData => {
  const c = 1;
  const k = 1;
  const loc = d.s_m!;
  const rot = d.q!;
  return {
    loc: new Vector3(loc.x! * c, loc.z! * c, -loc.y! * c),
    // rot: new Quaternion(rot.j! * k, rot.k! * k, rot.i! * k, rot.r!),
    rot: new Quaternion(rot.i! * k, rot.k! * k, -rot.j! * k, rot.r!),
  };
};

export type AnimationState = "playing" | "paused";
export type PositionState = "start" | "inbetween" | "end";

export class SkateboardModel {
  private currentFrame: number = 0;
  frameNum: number = 0;
  interval: number = 0;

  playData?: ST.Play;
  // @ts-ignore   `makeAutoObservable` will make this a IObservableArray..
  timeStamps: IObservableArray<number> = [];

  allPosData: PosData[] = [];

  // State Machine
  animationState: AnimationState = "paused";
  positionState: PositionState = "start";

  constructor() {
    makeAutoObservable(this);
  }

  get frame() {
    return this.currentFrame;
  }
  set frame(newVal: number) {
    this.currentFrame = Math.max(0, Math.min(this.frameNum, newVal));
    this.updatePositionState();
  }

  get posData(): PosData | undefined {
    if (this.allPosData.length <= this.currentFrame) return undefined;
    return this.allPosData[this.currentFrame];
  }
  get timeStamp(): number | undefined {
    if (this.timeStamps.length <= this.currentFrame) return undefined;
    return this.timeStamps[this.currentFrame];
  }

  setFrom(playData?: ST.Play | null) {
    this.currentFrame = 0;
    this.frameNum = 0;
    this.interval = 0;
    this.playData = playData ?? undefined;
    this.timeStamps.clear();
    this.allPosData = [];
    this.animationState = "paused";
    this.positionState = "start";

    const steps = playData?.steps ?? [];
    if (steps.length) {
      // transform position data
      let smallestY = 0; // only zero if values < 0 are found
      const posData = steps.map((step) => {
        this.timeStamps.push(step.t_ms!);
        const ret = Step2PosData(step);
        smallestY = Math.min(smallestY, ret.loc.y);
        return ret;
      });
      smallestY = Math.abs(smallestY);
      posData.forEach((d) => (d.loc.y += smallestY));
      this.allPosData = posData;

      // extract intervals
      this.frameNum = this.timeStamps.length - 1;
      this.interval =
        _.sum(
          _.zip(this.timeStamps, this.timeStamps.slice(1)).map(
            ([first, second]) =>
              first === undefined || second === undefined ? -1 : second - first
          )
        ) / this.timeStamps.length;
    }
  }

  setInterval(val: number) {
    this.interval = val;
  }

  logState(context: string = "") {
    logger.info(`[SkateboardModel] ${context} | animationState: ${this.animationState}, positionState: ${this.positionState}, frame: ${this.currentFrame}, frameNum: ${this.frameNum}, interval: ${this.interval}`);
  }

  // State Machine Methoden
  play() {
    const oldAnimationState = this.animationState;
    const oldPositionState = this.positionState;
    // NUR bei "end" zurück zu start springen, sonst aktuelle Position beibehalten
    if (this.positionState === "end") {
      this.currentFrame = 0;
      this.positionState = "start";
    }
    this.animationState = "playing";
    if (oldAnimationState !== this.animationState || oldPositionState !== this.positionState) {
      this.logState("play() called");
    }
  }
  pause() {
    const oldAnimationState = this.animationState;
    this.animationState = "paused";
    if (oldAnimationState !== this.animationState) {
      this.logState("pause() called");
    }
  }
  reset() {
    const oldAnimationState = this.animationState;
    const oldPositionState = this.positionState;
    const oldFrame = this.currentFrame;
    this.currentFrame = 0;
    this.animationState = "paused";
    this.positionState = "start";
    if (oldAnimationState !== this.animationState || oldPositionState !== this.positionState || oldFrame !== this.currentFrame) {
      this.logState("reset() called");
    }
  }
  end() {
    const oldAnimationState = this.animationState;
    const oldPositionState = this.positionState;
    const oldFrame = this.currentFrame;
    this.currentFrame = this.frameNum;
    this.animationState = "paused";
    this.positionState = "end";
    if (oldAnimationState !== this.animationState || oldPositionState !== this.positionState || oldFrame !== this.currentFrame) {
      this.logState("end() called");
    }
  }
  updatePositionState() {
    const oldPositionState = this.positionState;
    if (this.currentFrame === 0) {
      this.positionState = "start";
    } else if (this.currentFrame >= this.frameNum) {
      this.positionState = "end";
    } else {
      this.positionState = "inbetween";
    }
    // Nur loggen wenn sich der positionState geändert hat
    if (oldPositionState !== this.positionState) {
      this.logState(`positionState changed: ${oldPositionState} -> ${this.positionState}`);
    }
  }
}

// export const SkateboardModel = types
//   .model({
//     frame: types.optional(types.number, 0),
//     frameNum: types.optional(types.number, 0),
//     interval: types.optional(types.number, 0),

//     playData: types.frozen<ST.Play | undefined>(),

//     timeStamps: types.array(types.number),
//   })
//   .views((self) => {
//     let lastPosData: PosData | null = null;
//     return {
//       get posData() {
//         if (!self.playData?.steps) return null;
//         if (!self.lastPosData)
//           self.lastPosData = Step2PosData(self.playData.steps[self.frame]);
//         return lastPosData;
//       },
//     };
//   })
//   .actions((self) => ({
//     setFrom(playData?: ST.Play) {
//       self.frame = 0;
//       self.interval = 0;
//       self.playData = playData;

//       const steps = playData?.steps ?? [];
//       self.timeStamps.replace(steps.map((s) => s.t_ms!));
//       self.frameNum = Math.max(0, steps.length - 1);

//       if (self.timeStamps.length) {
//         self.interval =
//           _.sum(
//             _.zip(timeStamps, timeStamps.slice(1)).map(([first, second]) =>
//               first === undefined || second === undefined ? -1 : second - first
//             )
//           ) / timeStamps.length;
//       }
//     },
//     setFrame(value: number) {
//       self.frame = value;
//     },
//   }));

// export type SkateboardStore = Instance<typeof SkateboardModel>;
export type SkateboardStore = SkateboardModel;

export default SkateboardModel;
